import React, { useEffect } from "react";
import { Minus, Plus, Gift, Check } from "lucide-react";
import { API, money } from "../../lib/api";
import { useT } from "../../lib/i18n";

const IMG_GIFT = "/brand/love-gift.jpg";

// Max selectable quantity for an add-on. Gift Packaging keeps floor(qty/6).
function maxFor(addon, kitQty) {
  if (addon.special === "gift") return Math.floor((Number(kitQty) || 0) / 6);
  if (!addon.quantity_enabled) return 1;
  return Number(kitQty) || 0;
}

export default function ExtrasStep({ draft, setDraft, settings }) {
  const { t } = useT();
  const addons = settings?.add_ons || [];
  const selected = draft.add_ons || {};

  // Clamp any selections that exceed the current max (e.g. quantity changed).
  useEffect(() => {
    let changed = false;
    const next = { ...selected };
    addons.forEach((a) => {
      const mx = maxFor(a, draft.kit_qty);
      const cur = Number(next[a.id] || 0);
      if (cur > mx) { next[a.id] = mx; changed = true; }
    });
    if (changed) setDraft((d) => ({ ...d, add_ons: next }));
  }, [draft.kit_qty]); // eslint-disable-line react-hooks/exhaustive-deps

  const setQty = (id, n, mx) => {
    let v = Math.round(Number(n) || 0);
    if (v < 0) v = 0;
    if (v > mx) v = mx;
    setDraft((d) => ({ ...d, add_ons: { ...(d.add_ons || {}), [id]: v } }));
  };

  if (addons.length === 0) {
    return <p className="py-6 text-center text-sm text-[hsl(var(--muted-foreground))]">{t("gift_optional")}</p>;
  }

  return (
    <div className="space-y-4" data-testid="extras-step">
      {addons.map((a) => {
        const mx = maxFor(a, draft.kit_qty);
        const count = Math.min(Number(selected[a.id] || 0), mx);
        const img = a.has_media ? `${API}/media/addon/${a.id}?v=${a.version}` : (a.special === "gift" ? IMG_GIFT : null);
        return (
          <div key={a.id} data-testid={`addon-${a.id}`} className="rounded-[1.1rem] border bg-card p-4 shadow-[var(--shadow-card)]">
            <div className="flex gap-3">
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-[hsl(var(--muted))]">
                {img ? <img src={img} alt={a.title} className="h-full w-full object-cover" /> : <span className="grid h-full w-full place-items-center"><Gift className="h-7 w-7" style={{ color: "hsl(var(--brand-rose))" }} /></span>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-display text-base font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{a.title}{a.special === "gift" ? " 🎁" : ""}</div>
                  <div className="shrink-0 text-right text-sm font-semibold tabular-nums" style={{ color: "hsl(var(--brand-rose))" }}>{money(a.price_cents)}</div>
                </div>
                {a.description && <p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">{a.description}</p>}

                <div className="mt-3">
                  {a.quantity_enabled ? (
                    <div className="flex items-center gap-3">
                      <button type="button" aria-label="Decrease" data-testid={`addon-${a.id}-minus`} onClick={() => setQty(a.id, count - 1, mx)} disabled={count <= 0} className="grid h-10 w-10 place-items-center rounded-xl border bg-white text-[hsl(var(--brand-brown))] disabled:opacity-40"><Minus className="h-4 w-4" /></button>
                      <span className="min-w-8 text-center font-display text-xl font-semibold tabular-nums" data-testid={`addon-${a.id}-count`}>{count}</span>
                      <button type="button" aria-label="Increase" data-testid={`addon-${a.id}-plus`} onClick={() => setQty(a.id, count + 1, mx)} disabled={count >= mx} className="grid h-10 w-10 place-items-center rounded-xl border bg-white text-[hsl(var(--brand-brown))] disabled:opacity-40"><Plus className="h-4 w-4" /></button>
                      {a.special === "gift" && <span className="ml-1 text-[11px] text-[hsl(var(--muted-foreground))]">{t("gift_max_note", { n: mx, qty: draft.kit_qty })}</span>}
                    </div>
                  ) : (
                    <button type="button" data-testid={`addon-${a.id}-toggle`} onClick={() => setQty(a.id, count > 0 ? 0 : 1, 1)} className={`inline-flex h-10 items-center gap-1.5 rounded-xl border px-4 text-sm font-semibold ${count > 0 ? "text-white" : "text-[hsl(var(--brand-brown))]"}`} style={count > 0 ? { background: "hsl(var(--brand-rose))", borderColor: "hsl(var(--brand-rose))" } : {}}>
                      {count > 0 ? <><Check className="h-4 w-4" /> {t("addon_added")}</> : t("addon_add")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <p className="text-center text-xs text-[hsl(var(--muted-foreground))]">{t("gift_optional")}</p>
    </div>
  );
}
